/**
 * @description 完全清理 reCAPTCHA（处理动态加载的脚本）
 */
const cleanupRecaptcha = () => {
  console.log('🧹 开始清理 reCAPTCHA...');

  // 1. 删除所有 reCAPTCHA 相关脚本（包括动态加载的）
  const scripts = document.querySelectorAll('script[src*="recaptcha"], script[src*="gstatic"]');
  scripts.forEach(script => {
    console.log('删除脚本:', script.src);
    script.remove();
  });

  // 2. 删除 reCAPTCHA badge（右下角的图标）
  const badges = document.querySelectorAll('.grecaptcha-badge');
  badges.forEach(badge => {
    console.log('删除 badge');
    badge.remove();
  });

  // 3. 删除所有相关的 iframe
  const iframes = document.querySelectorAll('iframe[src*="recaptcha"], iframe[src*="google.com/recaptcha"], iframe[title*="recaptcha"]');
  iframes.forEach(iframe => {
    console.log('删除 iframe:', iframe.src || iframe.title);
    iframe.remove();
  });

  // 4. 删除可能的容器元素（保留 recaptcha-container）
  const containers = document.querySelectorAll('[id*="recaptcha"]:not(#recaptcha-container), [class*="recaptcha"]');
  containers.forEach(container => {
    console.log('删除容器:', container.id || container.className);
    container.remove();
  });

  // 5. 清除全局对象（最关键！）
  if (window.grecaptcha) {
    console.log('清除 window.grecaptcha');
    delete window.grecaptcha;
  }

  // 6. 清除内部配置对象
  if (window.___grecaptcha_cfg) {
    console.log('清除 window.___grecaptcha_cfg');
    delete window.___grecaptcha_cfg;
  }

  // 7. 清除可能的回调队列
  if (window.recaptchaOptions) {
    console.log('清除 window.recaptchaOptions');
    delete window.recaptchaOptions;
  }

  console.log('✅ reCAPTCHA 清理完成');
};

/**
 * @description 获取谷歌验证码V3
 * @returns {Promise<string>}
 */
export const generateVerificationGoogleTokenV3 = () => {
  return new Promise((resolve, reject) => {
    if (!window.grecaptcha || !window.grecaptcha.execute) {
      reject(new Error('reCAPTCHA v3 未加载'));
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.VUE_APP_RECAPTCHA_SITE_KEY_V3, { action: 'submit_form' })
        .then((token) => {
          console.log('✅ v3 token 获取成功');
          resolve(token);
        })
        .catch((error) => { // 修复：.cache → .catch
          console.error('❌ v3 token 生成失败:', error);
          reject(error);
        });
    });
  });
};

/**
 * @description 获取谷歌验证码V2
 * @param {string} dom - DOM 元素的 ID
 * @returns {Promise<string>}
 */
export const generateVerificationGoogleTokenV2 = (dom) => {
  return new Promise((resolve, reject) => {
    if (!window.grecaptcha || !window.grecaptcha.render) {
      reject(new Error('reCAPTCHA v2 未加载'));
      return;
    }

    const container = document.getElementById(dom);

    if (!container) {
      reject(new Error(`找不到元素: #${dom}`));
      return;
    }

    // 清空容器（防止重复渲染）
    container.innerHTML = '';

    try {
      window.grecaptcha.render(dom, {
        sitekey: process.env.VUE_APP_RECAPTCHA_SITE_KEY_V2,
        callback: (token) => {
          console.log('✅ v2 token 获取成功');
          resolve(token);
        },
        'error-callback': (error) => {
          console.error('❌ v2 验证失败:', error);
          reject(error);
        },
        'expired-callback': () => {
          console.warn('⚠️ v2 验证已过期');
          reject(new Error('验证已过期'));
        }
      });
    } catch (error) {
      console.error('❌ v2 渲染失败:', error);
      reject(error);
    }
  });
};

/**
 * @description 加载谷歌验证码v3 script
 * @returns {Promise<void>}
 */
export const loadGoogleCaptchaV3Script = () => {
  return new Promise((resolve, reject) => {
    // 检查是否已经加载
    if (window.grecaptcha && window.grecaptcha.execute) {
      console.log('⚠️ reCAPTCHA v3 已加载，跳过');
      resolve();
      return;
    }

    // 延迟加载，确保清理完成
    setTimeout(() => {
      const script = document.createElement('script');
      script.id = 'recaptcha-v3-script';
      script.src = `https://www.recaptcha.net/recaptcha/api.js?render=${process.env.VUE_APP_RECAPTCHA_SITE_KEY_V3}`;

      script.onload = () => {
        console.log('✅ reCAPTCHA v3 脚本加载成功');

        // 等待 grecaptcha 对象就绪
        const checkReady = setInterval(() => {
          if (window.grecaptcha && window.grecaptcha.ready) {
            clearInterval(checkReady);

            window.grecaptcha.ready(() => {
              console.log('✅ reCAPTCHA v3 完全就绪');
              resolve();
            });
          }
        }, 50);

        // 超时保护（5秒）
        setTimeout(() => {
          clearInterval(checkReady);
          if (!window.grecaptcha || !window.grecaptcha.ready) {
            reject(new Error('reCAPTCHA v3 加载超时'));
          }
        }, 5000);
      };

      script.onerror = (error) => {
        console.error('❌ reCAPTCHA v3 加载失败:', error);
        cleanupRecaptcha();
        reject(error);
      };

      document.head.appendChild(script);
    }, 200); // 延迟 200ms
  });
};

/**
 * @description 删除谷歌验证码v3 script
 */
export const deleteGoogleCaptchaV3Script = () => {
  console.log('🗑️ 删除 reCAPTCHA v3');
  cleanupRecaptcha();
};

/**
 * @description 加载谷歌验证码v2 script
 * @returns {Promise<void>}
 */
export const loadGoogleCaptchaV2Script = () => {
  return new Promise((resolve, reject) => {
    // 检查是否已经加载
    if (window.grecaptcha && window.grecaptcha.render) {
      console.log('⚠️ reCAPTCHA v2 已加载，跳过');
      resolve();
      return;
    }

    setTimeout(() => {
      const script = document.createElement('script');
      script.id = 'recaptcha-v2-script';
      // v2 使用 render=explicit
      script.src = 'https://www.recaptcha.net/recaptcha/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('✅ reCAPTCHA v2 脚本加载成功');

        const checkReady = setInterval(() => {
          if (window.grecaptcha && window.grecaptcha.render) {
            clearInterval(checkReady);
            console.log('✅ reCAPTCHA v2 完全就绪');
            resolve();
          }
        }, 50);

        setTimeout(() => {
          clearInterval(checkReady);
          if (!window.grecaptcha || !window.grecaptcha.render) {
            reject(new Error('reCAPTCHA v2 加载超时'));
          }
        }, 5000);
      };

      script.onerror = (error) => {
        console.error('❌ reCAPTCHA v2 加载失败:', error);
        cleanupRecaptcha();
        reject(error);
      };

      document.head.appendChild(script);
    }, 200);
  });
};

/**
 * @description 删除谷歌验证码v2 script
 */
export const deleteGoogleCaptchaV2Script = () => {
  console.log('🗑️ 删除 reCAPTCHA v2');
  cleanupRecaptcha();
};

/**
 * @description 切换 reCAPTCHA 版本（推荐使用）
 * @param {string} version - 'v2' | 'v3'
 * @returns {Promise<void>}
 */
export const switchRecaptchaVersion = async(version) => {
  console.log(`🔄 切换到 reCAPTCHA ${version}`);

  try {
    // 完全清理
    cleanupRecaptcha();

    // 等待清理完成
    await new Promise(resolve => setTimeout(resolve, 300));

    // 加载新版本
    if (version === 'v2') {
      await loadGoogleCaptchaV2Script();
    } else if (version === 'v3') {
      await loadGoogleCaptchaV3Script();
    } else {
      throw new Error(`不支持的版本: ${version}`);
    }

    console.log(`✅ 成功切换到 ${version}`);
  } catch (error) {
    console.error(`❌ 切换到 ${version} 失败:`, error);
    throw error;
  }
};
